import { cn } from "@base/lib/utils";
import { useCurrentUserQuery, useDeleteConfirmDialog } from "@base/model/hooks";
import { useDeleteDocumentMutation } from "@base/model/hooks/useDocumentsQuery";
import { AppLink } from "@base/ui/AppLink";
import { DateTile } from "@base/ui/DateTile";
import { Button, Dropdown, Popover } from "@buildblockinc/buildblock-design-system";
import { useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { EllipsisVertical, Lock } from "lucide-react";
import { useState } from "react";
import type { DailyLogSearchReturnState } from "../model/dailyLogSearchNavigation";
import { invalidateDailyLogSearch } from "../model/dailyLogSearchQueries";
import { refreshActiveDailyLogSearch } from "../model/dailyLogSearchRefresh";
import type { DailyLogSearchItem } from "../model/dailyLogSearchSchema";
import { DailyLogSearchExcerpt } from "./DailyLogSearchExcerpt";
import { DocFileIcon } from "./DocFileIcon";

const VISIBLE_LABEL_COUNT = 2;

interface DailyLogSearchRowProps {
  projectId: number;
  item: DailyLogSearchItem;
  hasKeyword: boolean;
  /** 새 조건의 첫 응답 전에는 이전 조건의 행으로 상세에 진입하지 않게 막는다 */
  disabled: boolean;
  /** 상세의 닫기 버튼이 이 목록으로 돌아올 수 있게 넘기는 history state */
  linkState: DailyLogSearchReturnState;
  /** 상세 링크 사용. 네이티브 링크 동작(새 탭 등)은 막지 않는다 */
  onOpen: (openMethod: "same_tab" | "new_tab") => void;
  /** 메뉴의 Edit로 같은 탭에서 떠날 때 (목록 위치 저장) */
  onEditOpen: () => void;
}

const MIDDLE_MOUSE_BUTTON = 1;

function isNewTabClick(event: React.MouseEvent) {
  return event.metaKey || event.ctrlKey || event.shiftKey || event.button === MIDDLE_MOUSE_BUTTON;
}

/**
 * 일지 한 행. 주요 텍스트 전체가 상세 링크 하나이고,
 * 업체·태그 전체 보기와 편집·삭제 메뉴는 링크 밖의 별도 버튼이다.
 */
export function DailyLogSearchRow({
  projectId,
  item,
  hasKeyword,
  disabled,
  linkState,
  onOpen,
  onEditOpen,
}: DailyLogSearchRowProps) {
  const logId = String(item.id);
  const date = dayjs(item.date);
  const lockLabel = item.locked && item.modifier ? `Being edited by ${item.modifier.name}` : null;

  return (
    <li
      className="flex gap-2 items-center md:gap-4 md:items-start md:px-3 md:py-4 md:border-b md:border-gray-100 md:last:border-b-0 md:hover:bg-gray-10"
      data-daily-log-id={item.id}
      data-daily-log-date={item.date}
      aria-disabled={disabled || undefined}
    >
      <DateTile date={item.date} />
      <div className="flex flex-col flex-1 gap-2.5 min-w-0">
        <AppLink
          to="/projects/$projectId/log/$logId"
          params={{ projectId: String(projectId), logId }}
          state={{ dailyLogSearchReturn: linkState }}
          disabled={disabled}
          onClick={(event) => onOpen(isNewTabClick(event) ? "new_tab" : "same_tab")}
          onAuxClick={(event) => {
            if (event.button === MIDDLE_MOUSE_BUTTON) onOpen("new_tab");
          }}
          className={cn(
            "flex gap-2 items-center py-2 rounded-8 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-500 md:py-0",
            disabled && "pointer-events-none opacity-60",
          )}
        >
          <MobileDocIcon locked={item.locked} />
          <div className="flex flex-col flex-1 gap-1 min-w-0 md:gap-1.5">
            {lockLabel && <span className="text-label3 text-red-300 md:hidden">{lockLabel}</span>}
            <div className="flex gap-2 items-center min-w-0">
              <p className="truncate text-body4 md:text-subtitle4 md:text-gray-1000">
                {item.title || `Daily log · ${date.format("MMM D, YYYY")}`}
              </p>
              {lockLabel && <LockBadge label={lockLabel} />}
            </div>
            <p className="hidden text-label3 text-gray-600 md:block">
              <time dateTime={item.date}>{date.format("ddd, MMM D, YYYY")}</time>
              <span aria-hidden="true"> · </span>
              {item.writer?.name ?? "Unknown writer"}
            </p>
            {/* 모바일은 제목만 보여 주고, 검색어가 있을 때만 일치 문맥을 함께 보여 준다 */}
            <div className={cn(!hasKeyword && "hidden md:block")}>
              <DailyLogSearchExcerpt excerpt={item.excerpt} hasKeyword={hasKeyword} />
            </div>
          </div>
        </AppLink>

        <div className="hidden flex-wrap gap-x-3 gap-y-1 md:flex">
          <LabelSummary kind="Contractors" labels={item.contractors} />
          <LabelSummary kind="Status" labels={item.tags} />
        </div>
      </div>

      <DailyLogSearchRowMenu
        projectId={projectId}
        item={item}
        disabled={disabled}
        linkState={linkState}
        onEditOpen={onEditOpen}
      />
    </li>
  );
}

/** 모바일은 초안·기존 문서 목록과 같은 문서 아이콘을 쓴다. 잠긴 일지는 자물쇠를 겹쳐 표시한다 */
function MobileDocIcon({ locked }: { locked: boolean }) {
  return (
    <div className="relative shrink-0 md:hidden">
      <DocFileIcon />
      {locked && (
        <img
          src="/assets/images/icons/lock.svg"
          alt=""
          aria-hidden="true"
          className="absolute left-[9px] top-[9px] size-[13px] pointer-events-none"
        />
      )}
    </div>
  );
}

function LockBadge({ label }: { label: string }) {
  return (
    <span className="hidden gap-1 items-center px-2 py-0.5 whitespace-nowrap rounded-circle shrink-0 bg-red-10 text-label3 text-red-600 md:inline-flex">
      <Lock width={12} height={12} aria-hidden="true" />
      {label}
    </span>
  );
}

interface LabelSummaryProps {
  kind: "Contractors" | "Status";
  labels: { id: number; name: string }[];
}

function LabelSummary({ kind, labels }: LabelSummaryProps) {
  const [isAllLabelsOpen, setIsAllLabelsOpen] = useState(false);
  if (labels.length === 0) return null;

  const visibleLabels = labels.slice(0, VISIBLE_LABEL_COUNT);
  const hiddenCount = labels.length - visibleLabels.length;

  return (
    <div className="flex flex-wrap gap-1 items-center min-w-0 text-label3">
      <span className="text-gray-600">{kind}</span>
      {visibleLabels.map((label) => (
        <span
          key={label.id}
          className={cn(
            "px-2 py-0.5 max-w-[160px] truncate rounded-2",
            kind === "Status" ? "text-blue-600 bg-blue-10" : "text-gray-800 bg-gray-100",
          )}
          title={label.name}
        >
          {label.name}
        </span>
      ))}
      {hiddenCount > 0 && (
        <Popover open={isAllLabelsOpen} onOpenChange={(details) => setIsAllLabelsOpen(details.open)}>
          <Popover.Trigger
            className="px-2 py-0.5 text-gray-700 bg-white rounded-2 border border-gray-200"
            aria-label={`Show all ${labels.length} ${kind === "Status" ? "statuses" : "contractors"}`}
          >
            {`+${hiddenCount}`}
          </Popover.Trigger>
          {isAllLabelsOpen && (
            <Popover.Content className="p-3 max-w-[280px]">
              <p className="mb-2 text-label2 text-gray-1000">{kind}</p>
              <ul className="flex flex-col gap-1">
                {labels.map((label) => (
                  <li key={label.id} className="text-body4 text-gray-800 break-words">
                    {label.name}
                  </li>
                ))}
              </ul>
            </Popover.Content>
          )}
        </Popover>
      )}
    </div>
  );
}

interface DailyLogSearchRowMenuProps {
  projectId: number;
  item: DailyLogSearchItem;
  disabled: boolean;
  linkState: DailyLogSearchReturnState;
  onEditOpen: () => void;
}

function DailyLogSearchRowMenu({ projectId, item, disabled, linkState, onEditOpen }: DailyLogSearchRowMenuProps) {
  const queryClient = useQueryClient();
  const { openDeleteDialog, close } = useDeleteConfirmDialog();
  const { mutateAsync: deleteDocument } = useDeleteDocumentMutation();
  const { data: currentUserData } = useCurrentUserQuery();
  const currentUserId = currentUserData?.user.id;

  // 목록의 잠금 표시는 참고 정보다. 실제 편집 가능 여부는 편집 화면의 잠금 취득이 판단한다.
  const isLockedByOther = !!(item.locked && item.modifier && currentUserId && item.modifier.id !== currentUserId);

  const handleDelete = () => {
    openDeleteDialog({
      description: "You will be unable to recover this report.",
      onDelete: () =>
        deleteDocument(
          { projectId, documentId: item.id, type: "DAILY_LOG" },
          {
            onSuccess: () => {
              invalidateDailyLogSearch(queryClient, projectId);
              void refreshActiveDailyLogSearch(projectId);
              close();
            },
          },
        ),
    });
  };

  return (
    <Dropdown>
      <Dropdown.Trigger
        className="p-1 text-gray-700 rounded-8 shrink-0 md:hover:bg-gray-100"
        disabled={disabled}
        aria-label="Daily log actions"
      >
        <EllipsisVertical width={20} height={20} />
      </Dropdown.Trigger>
      <Dropdown.Content className="p-2">
        <Dropdown.Item value="edit">
          <AppLink
            to="/projects/$projectId/log/$logId/edit"
            params={{ projectId: String(projectId), logId: String(item.id) }}
            // 편집 → 저장 후 상세 → 닫기, 또는 편집 취소 시 이 목록의 조건·위치로 돌아온다.
            state={{ dailyLogSearchReturn: linkState }}
            onClick={(event) => {
              if (!isNewTabClick(event)) onEditOpen();
            }}
            disabled={isLockedByOther}
          >
            <span className={cn("inline-block px-2 py-3", isLockedByOther && "text-gray-400")}>Edit Report</span>
          </AppLink>
        </Dropdown.Item>
        {item.is_owner && (
          <Dropdown.Item value="delete">
            <Button
              type="button"
              className="px-2 py-3 text-red-500"
              variant="ghost"
              color="secondary"
              size="large"
              onClick={handleDelete}
            >
              <span className="leading-none">Delete Report</span>
            </Button>
          </Dropdown.Item>
        )}
      </Dropdown.Content>
    </Dropdown>
  );
}
